"use strict";

const Moment = require('moment');

// TODO: move this out of here
const UPLYNK_DEFAULT_LICENSE_SERVER = 'https://content.uplynk.com/wv';
const models = ['BrightSign', 'XUMO', 'AndroidSkykit', 'Skykit', 'XI6'];

/**
 * Normalizes and validates asset type for Uplynk stream URLs
 * @param {string|any} type - The asset type to normalize
 * @returns {string} - Returns 'channel', 'playlist', or 'event' (default)
 */
const getAssetType = type => {
  const normalized = typeof type === 'string' ? type.toLowerCase() : '';
  return ['channel', 'playlist', 'event'].includes(normalized) ? normalized : 'event';
};
const getMetadata = stream => {
  const playerURL = new URL('https://everpass-player.upshow.tv');
  const {
    rooftopId,
    orgId
  } = stream;
  if (rooftopId) {
    playerURL.searchParams.set('rooftopId', rooftopId);
  }
  if (orgId) {
    playerURL.searchParams.set('orgId', orgId);
  }
  if (stream.provider === 'uplynk') {
    const {
      useDrm,
      licenseServer,
      streamId,
      assetType
    } = stream;
    playerURL.searchParams.set('cmcd', 'default');
    const typePath = getAssetType(assetType);
    return {
      url: `${playerURL.toString()}`,
      streamId,
      isSecure: false,
      hasDrm: useDrm,
      provider: stream.provider,
      licenseServer: licenseServer || UPLYNK_DEFAULT_LICENSE_SERVER,
      useMux: true,
      streamURL: `https://content.uplynk.com/${typePath}/${streamId}.m3u8?expand=ad,gam_params,drm_wv_l3&rays=cabeg` // rays profile for standard device
    };
  } else if (stream.provider === 'custom') {
    const {
      useDrm,
      licenseServer,
      streamId,
      streamURL
    } = stream;
    return {
      url: `${playerURL.toString()}`,
      streamId,
      isSecure: false,
      hasDrm: useDrm,
      provider: stream.provider,
      licenseServer: licenseServer,
      useMux: true,
      streamURL
    };
  } else if (stream.provider === 'victory') {
    const {
      streamId,
      streamURL,
      expectedResolution,
      externalRecordId
    } = stream;
    return {
      url: `${playerURL.toString()}`,
      streamId,
      provider: stream.provider,
      useMux: true,
      streamURL,
      expectedResolution,
      externalRecordId
    };
  } else {
    const {
      hasDrm,
      isSecure,
      expectedResolution,
      url,
      streamId,
      signUrl
    } = stream;
    playerURL.searchParams.set('cmcd', 'default');
    let secureUrl;
    if (isSecure) secureUrl = 'https://stream.upshow.tv/auth?ticket=1&authCookieType=video';
    return {
      url: `${playerURL.toString()}`,
      streamURL: url,
      streamId,
      isSecure: signUrl ? false : isSecure,
      useMux: true,
      provider: 'EPM',
      licenseServer: null,
      hasDrm,
      expectedResolution,
      secureUrl: signUrl ? undefined : secureUrl,
      signUrl
    };
  }
};
const getCencVariants = streamUrl => [{
  model: 'BrightSign',
  signUrl: true,
  streamUrl: streamUrl
}, {
  model: 'XI6',
  signUrl: true,
  streamUrl: streamUrl
}];
const isNumber = value => !Number.isNaN(parseInt(value));
const parseBoolValue = value => {
  if (typeof value === 'string') {
    return ['true', '1'].includes(value.trim().toLowerCase());
  }
  return value === true || value === 1;
};
const stopWords = ['the', 'vs', 'and', 'of', 'in', 'for', 'on', 'at', 'with', 'a', 'an', 'to', 'by'];
const searchEventsWithFuse = (Fuse, events, searchQuery) => {
  const fuse = new Fuse(events, {
    keys: [{
      name: 'firstTeam.alias[0]',
      weight: 0.3
    }, {
      name: 'secondTeam.alias[0]',
      weight: 0.3
    }, {
      name: 'firstTeam.display_name',
      weight: 0.3
    }, {
      name: 'secondTeam.display_name',
      weight: 0.3
    }, {
      name: 'sportLabel',
      weight: 0.3
    }, {
      name: 'leagues[0].alias',
      weight: 0.3
    }, {
      name: 'sports[0].alias',
      weight: 0.3
    }, {
      name: 'information.gender',
      weight: 0.3
    }, {
      name: 'information.conference',
      weight: 0.3
    }, {
      name: 'title',
      weight: 0.1
    }, {
      name: 'description',
      weight: 0.1
    }, {
      name: 'name',
      weight: 0.1
    }],
    threshold: 0.3,
    includeScore: true
  });
  const terms = searchQuery.trim().split(' ').filter(word => !stopWords.includes(word.toLowerCase())).map(term => `^${term}`);
  const resultsMap = {};
  terms.forEach(term => {
    const termResults = fuse.search(term);
    termResults.forEach(({
      item,
      score
    }) => {
      if (!resultsMap[item.id]) {
        resultsMap[item.id] = {
          item,
          score: 0,
          matches: 0
        };
      }
      resultsMap[item.id].score += score;
      resultsMap[item.id].matches += 1;
    });
  });
  return Object.values(resultsMap).sort((a, b) => a.score - b.score).map(result => result.item);
};
const getGeneratedThumbnailPath = (information, subscriptionType) => {
  let sport;
  let league;
  let conferenceParam = '';
  let team1;
  let team2;
  if (information) {
    const {
      sport_type,
      league_type,
      conference,
      first_team,
      second_team
    } = information;
    sport = sport_type ? sport_type.toLowerCase() : undefined;
    league = league_type ? league_type.toLowerCase() : undefined;
    conferenceParam = conference ? `&conference=${conference}` : '';
    team1 = first_team || undefined;
    team2 = second_team || undefined;
  }
  const subscription = subscriptionType || undefined;
  return `${sport}/${league}/${team1}/${team2}?subscription_type=${subscription}${conferenceParam}`;
};
const getGeneratedThumbnailPathForUpdate = (teamData, information, subscriptionType) => {
  let sport;
  let league;
  let conferenceParam = '';
  if (information) {
    const {
      sport_type,
      league_type,
      conference
    } = information;
    sport = sport_type ? sport_type.toLowerCase() : undefined;
    league = league_type ? league_type.toLowerCase() : undefined;
    conferenceParam = conference ? `&conference=${conference}` : '';
  }
  const subscription = subscriptionType || undefined;
  const formattedColor = encodeURI(teamData.background_color);
  return `?sport=${sport}&league=${league}&teamOneAssetUrl=${teamData.logo}&teamTwoAssetUrl=${teamData.logo}&teamOneColor=${formattedColor}&teamTwoColor=${formattedColor}&subscription_type=${subscription}${conferenceParam}`;
};
const getScoreThreshold = query => {
  if (query.length > 8) return 0.09;
  if (query.length > 5) return 0.13;
  return 0.7;
};
const fuseSearch = (Fuse, data, keys, searchQuery) => {
  const fuse = new Fuse(data, {
    keys,
    threshold: 0.3,
    distance: 100,
    shouldSort: true,
    includeScore: true,
    includeMatches: true,
    ignoreFieldNorm: true,
    isCaseSensitive: false
  });
  const threshold = getScoreThreshold(searchQuery);
  const sortedResults = fuse.search(searchQuery.trim());
  return sortedResults.filter(res => res.score < threshold).map(res => res.item);
};
const mapValue = (key, obj) => {
  const items = obj[key];
  if (!Array.isArray(items)) return [];
  return items.flatMap(item => {
    const values = [];
    if (item.display_name) {
      values.push(item.display_name);
    }
    if (Array.isArray(item.alias)) {
      values.push(...item.alias);
    } else if (item.alias) {
      values.push(item.alias);
    }
    return values;
  });
};
const isEventInPillFilter = (event, pillFilter) => {
  const {
    sportFilter,
    leagueFilter,
    genderFilter,
    conferenceFilter
  } = pillFilter;
  if (sportFilter && sportFilter !== event.sports[0]?.alias.replaceAll(' ', '_')) return false;
  if (leagueFilter && leagueFilter !== event.leagues[0]?.alias.replaceAll(' ', '_')) return false;
  if (genderFilter && genderFilter !== event.gender) return false;
  if (conferenceFilter && conferenceFilter !== event.conferences[0]?.alias.replaceAll(' ', '_')) return false;
  return true;
};
const searchKeys = [{
  name: 'title',
  weight: 0.9
}, {
  name: 'teams',
  getFn: obj => mapValue('teams', obj),
  weight: 0.4
}, {
  name: 'sports',
  getFn: obj => mapValue('sports', obj),
  weight: 0.4
}, {
  name: 'leagues',
  getFn: obj => mapValue('leagues', obj),
  weight: 0.4
}, {
  name: 'conferences',
  getFn: obj => mapValue('conferences', obj),
  weight: 0.4
}, {
  name: 'category',
  weight: 0.4
}];
const eventsSearchKeys = [...searchKeys, {
  name: 'subscriptionType.display_name',
  getFn: obj => obj.subscriptionType?.display_name ?? '',
  weight: 0.5
}, {
  name: 'subscription_type',
  weight: 0.1
}, {
  name: 'sportLabel',
  weight: 0.4
}, {
  name: 'leagueLabel',
  weight: 0.4
}, {
  name: 'gender',
  weight: 0.3
}, {
  name: 'name',
  weight: 0.3
}];
const createUplynkDefaultVariants = () => {
  // first leaf is for streaming ops and the second one for dessleaf
  const leafIdVariants = [{
    leafId: 52128,
    forceUrl: false,
    streamParamOverrides: {
      'ad.vip': '52.55.194.159',
      expand: 'ad,gam_params,drm_wv_l3_cenc'
    }
  }, {
    leafId: 46516,
    forceUrl: false,
    streamParamOverrides: {
      'ad.vip': '52.55.194.159',
      expand: 'ad,gam_params,drm_wv_l3_cenc'
    }
  }];
  const modelGroup = {
    models,
    forceUrl: false,
    streamParamOverrides: {
      expand: 'ad,gam_params,drm_wv_l3_cenc'
    }
  };
  return [modelGroup, ...leafIdVariants];
};
const createEpmVariants = stream => {
  const {
    cencUrl,
    hasDrm,
    isSecure
  } = stream;
  if (!cencUrl) {
    console.warn(`[LiveEvent] Stream "${stream.name}" (id: ${stream.id}) has no cencUrl — skipping variant auto-population.`);
    return [];
  }
  const metadata = getMetadata({
    ...stream,
    url: cencUrl
  });
  const metadataOverrides = {
    hasDrm,
    isSecure,
    provider: metadata.provider,
    secureUrl: metadata.secureUrl,
    licenseServer: metadata.licenseServer,
    streamURL: metadata.streamURL
  };
  const XI6MetadataOverrides = isSecure ? {
    ...metadataOverrides,
    isSecure: false,
    secureUrl: null
  } : metadataOverrides;
  return [{
    models: models.filter(m => m !== 'XI6'),
    playerUrl: metadata.url,
    metadataOverrides
  }, {
    model: 'XI6',
    signUrl: isSecure,
    playerUrl: metadata.url,
    metadataOverrides: XI6MetadataOverrides
  }];
};

/**
 *
 * @param {*} m Moment instance
 * @param {*} bucket the bucket size in seconds. 30s by default
 * @returns ISO string timestamp
 */
function getTimestampBucket(m, bucket) {
  if (!bucket || !Moment.isMoment(m)) throw new Error('The input should be a moment instance and a bucket in seconds');
  const roundedSec = Math.floor(m.seconds() / bucket) * bucket;
  return m.seconds(roundedSec).milliseconds(0).toISOString();
}
module.exports = {
  getMetadata,
  searchEventsWithFuse,
  getGeneratedThumbnailPath,
  getGeneratedThumbnailPathForUpdate,
  eventsSearchKeys,
  searchKeys,
  fuseSearch,
  getTimestampBucket,
  getCencVariants,
  isEventInPillFilter,
  createEpmVariants,
  createUplynkDefaultVariants,
  isNumber,
  parseBoolValue
};